
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { getResponsiveImageUrl } from "@/utils/image-optimization";

interface RelatedImagesGridProps {
  pins: any[];
  className?: string;
}

export function RelatedImagesGrid({ pins, className }: RelatedImagesGridProps) {
  const [columnLayout, setColumnLayout] = useState<number[][]>([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Get number of columns based on screen size
  const getColumnCount = (width: number) => {
    if (width < 640) return 2;
    if (width < 768) return 3;
    if (width < 1024) return 4;
    return 5;
  };

  // Calculate heights for masonry layout
  const calculateLayout = () => {
    const columnCount = getColumnCount(windowWidth);
    
    // Create empty columns
    const columns: number[][] = Array.from({ length: columnCount }, () => []);
    
    // Get column with minimum height
    const getShortestColumnIndex = () => {
      const columnHeights = columns.map(column => 
        column.reduce((acc, curr) => acc + curr, 0)
      );
      return columnHeights.indexOf(Math.min(...columnHeights));
    };
    
    // Distribute pins to columns
    pins.forEach((pin) => {
      // Generate a semi-random height between 200-350px for visual variety
      const height = 200 + Math.floor((pin.title?.length || 10) * 5 % 150);
      
      // Add to shortest column
      const shortestColumn = getShortestColumnIndex();
      columns[shortestColumn].push(height);
    });
    
    setColumnLayout(columns);
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Recalculate layout when pins or window size changes
  useEffect(() => {
    calculateLayout();
  }, [pins, windowWidth]);

  if (!pins || pins.length === 0) {
    return null;
  }

  const columnCount = getColumnCount(windowWidth);

  return (
    <div className={cn("grid gap-4", className, {
      'grid-cols-2': columnCount === 2,
      'grid-cols-3': columnCount === 3,
      'grid-cols-4': columnCount === 4,
      'grid-cols-5': columnCount === 5,
    })}>
      {Array.from({ length: columnCount }).map((_, columnIndex) => (
        <div key={columnIndex} className="flex flex-col gap-4">
          {pins.filter((_, pinIndex) => pinIndex % columnCount === columnIndex).map((pin) => {
            // Find the pin's index to get the corresponding height
            const pinIndexInColumn = Math.floor(pins.indexOf(pin) / columnCount);
            const height = columnLayout[columnIndex]?.[pinIndexInColumn] || 250;
            
            return (
              <Link 
                key={pin.id} 
                to={`/pin/${pin.id}`} 
                className="block overflow-hidden rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                <div 
                  className="relative bg-gray-100 rounded-lg overflow-hidden"
                  style={{ height: `${height}px` }}
                >
                  <img
                    src={getResponsiveImageUrl(pin.image_url, 800)}
                    alt={pin.title || "Related pin"}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity duration-200 flex items-end p-3">
                    <span className="text-white font-medium line-clamp-2 text-sm">
                      {pin.title}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      ))}
    </div>
  );
}
